import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress, FormControlLabel, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

type EmailPreferences = {
  systemMessages: boolean;
  eventAdministratorMessages: boolean;
  traderMessages: boolean;
  itemInterestAlerts: boolean;
  eventChangeAlerts?: boolean;
  wantedItemChangeAlerts?: boolean;
  newEventAlerts?: boolean;
};

const defaultPreferences: EmailPreferences = {
  systemMessages: true,
  eventAdministratorMessages: true,
  traderMessages: true,
  itemInterestAlerts: true,
  eventChangeAlerts: true,
  wantedItemChangeAlerts: true,
  newEventAlerts: true,
};

export default function UnsubscribeEmailPage() {
  const [searchParams] = useSearchParams();
  const encodedEmail = searchParams.get('email') || '';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [preferences, setPreferences] = useState<EmailPreferences>(defaultPreferences);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(false);

  const hasAnyEnabled = useMemo(() => Object.values(preferences).some(Boolean), [preferences]);

  useEffect(() => {
    const load = async () => {
      if (!encodedEmail) {
        setError('This unsubscribe link is missing the email token.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await fetch(`${API_BASE}/api/email-unsubscribe?email=${encodeURIComponent(encodedEmail)}`);
        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.error || 'Unable to load unsubscribe details');
        }

        setEmail(data.email || '');
        setPreferences({ ...defaultPreferences, ...(data.preferences || {}) });
      } catch (err: any) {
        setError(err?.message || 'Unable to load unsubscribe details');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [encodedEmail]);

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');

      const response = await fetch(`${API_BASE}/api/email-unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ encodedEmail, preferences })
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Unable to update preferences');
      }

      setPreferences({ ...defaultPreferences, ...(data.preferences || {}) });
      setMessage(data.message || 'Your email preferences have been updated.');
      setComplete(!Object.values(data.preferences || {}).some(Boolean));
    } catch (err: any) {
      setError(err?.message || 'Unable to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');

      const response = await fetch(`${API_BASE}/api/email-unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ encodedEmail })
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Unable to unsubscribe');
      }

      setPreferences({ ...defaultPreferences, ...(data.preferences || {}) });
      setMessage(data.message || 'You have been unsubscribed from CrowdTrades email notifications.');
      setComplete(true);
    } catch (err: any) {
      setError(err?.message || 'Unable to unsubscribe');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', py: 5, px: 2 }}>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                Unsubscribe from email notifications
              </Typography>
              <Typography color="text.secondary">
                This page controls personal email delivery from CrowdTrades. Your in-app mailbox will still keep working.
              </Typography>
            </Box>

            {loading ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={22} />
                <Typography color="text.secondary">Loading unsubscribe details...</Typography>
              </Stack>
            ) : (
              <>
                {error && <Alert severity="error">{error}</Alert>}
                {message && <Alert severity="success">{message}</Alert>}

                {!error && (
                  <>
                    <Alert severity={hasAnyEnabled ? 'info' : 'success'}>
                      {hasAnyEnabled
                        ? `Email notifications are currently enabled for ${email || 'this address'}.`
                        : `Email notifications are already turned off for ${email || 'this address'}.`}
                    </Alert>

                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'rgba(102,126,234,0.03)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        What this will do
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Manage the same email preferences you see in your profile page for <strong>{email || 'this email address'}</strong>.
                      </Typography>
                      <Stack spacing={0.5}>
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(preferences.systemMessages)} onChange={(e) => setPreferences((current) => ({ ...current, systemMessages: e.target.checked }))} />}
                          label="System messages"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(preferences.eventAdministratorMessages)} onChange={(e) => setPreferences((current) => ({ ...current, eventAdministratorMessages: e.target.checked }))} />}
                          label="Event administrator messages"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(preferences.traderMessages)} onChange={(e) => setPreferences((current) => ({ ...current, traderMessages: e.target.checked }))} />}
                          label="Messages from other traders"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(preferences.itemInterestAlerts)} onChange={(e) => setPreferences((current) => ({ ...current, itemInterestAlerts: e.target.checked }))} />}
                          label="Interest in my items"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(preferences.eventChangeAlerts)} onChange={(e) => setPreferences((current) => ({ ...current, eventChangeAlerts: e.target.checked }))} />}
                          label="Event changes for events I'm in"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(preferences.wantedItemChangeAlerts)} onChange={(e) => setPreferences((current) => ({ ...current, wantedItemChangeAlerts: e.target.checked }))} />}
                          label="Changes to items on my want list"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(preferences.newEventAlerts)} onChange={(e) => setPreferences((current) => ({ ...current, newEventAlerts: e.target.checked }))} />}
                          label="New event alerts near my selected area"
                        />
                      </Stack>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSavePreferences}
                        disabled={saving}
                        sx={{ textTransform: 'none' }}
                      >
                        {saving ? 'Saving...' : 'Save email preferences'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleUnsubscribeAll}
                        disabled={saving || complete || !hasAnyEnabled}
                        sx={{ textTransform: 'none' }}
                      >
                        {saving ? 'Unsubscribing...' : hasAnyEnabled ? 'Turn off all email notifications' : 'Already unsubscribed'}
                      </Button>
                    </Stack>
                  </>
                )}
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
